import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { JOIN_CLASS_PARAM } from '@examdojo/class';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { first, map, of, switchMap } from 'rxjs';
import { StudentClassService } from '../student-class/student-class.service';

export const forceJoinClassGuard: CanActivateFn = (route) => {
  const code = route.queryParams[JOIN_CLASS_PARAM];

  if (!code) {
    return true;
  }

  const featureFlagService = inject(FeatureFlagService<ExamdojoFeatureFlags>);
  const studentClassService = inject(StudentClassService);
  const router = inject(Router);
  const errorHandlerService = inject(ErrorHandlerService);

  return featureFlagService.initialized$.pipe(
    first(Boolean),
    switchMap(() => {
      if (!featureFlagService.get(ExamDojoFeatureFlag.Classes)) {
        return of(true);
      }

      return studentClassService.joinClass(code).pipe(
        map((result) => {
          if (!result) {
            return router.createUrlTree(['/classes']);
          }

          const slug = studentClassService.buildSlug(result.name, result.id);
          return router.createUrlTree(['/classes', slug]);
        }),
        errorHandlerService.catchError(
          `[ForceJoinClassGuard.joinClass]: failed to join class`,
          () => of(router.createUrlTree(['/classes'])),
          {
            toast: (err) => ({
              title: 'An error occurred while joining the class',
              description: (err as Error)?.message,
            }),
          },
        ),
      );
    }),
  );
};
